import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const RULES = [
  {
    suit: "♦",
    title: "Consent. Every time.",
    body: "Consent is the foundation of Players Lounge. Never assume. Ask when you need to. Pay attention to the answer. Respect boundaries immediately. No means no. Hesitation isn't yes. Anyone can change their mind at any point. Behaviour that makes another guest feel unsafe or persistently uncomfortable isn't welcome.",
  },
  {
    suit: "♠",
    title: "Phones stay out of play.",
    body: "Discretion matters. There is a strict no-photography or filming policy. Phones are put away once the evening moves beyond the social opening. What happens in the Lounge stays in the Lounge.",
  },
  {
    suit: "♥",
    title: "Come ready to play — not wasted.",
    body: "Poppers are permitted. Other recreational drugs aren't part of the game. If you're significantly intoxicated or your behaviour is affecting the experience or safety of other Players, you'll be asked to leave. The hosts reserve the right to refuse admission.",
  },
  {
    suit: "♣",
    title: "Arrive on time.",
    body: "Players Lounge works because everyone begins together. Please arrive at the advertised start time rather than treating the gathering like a venue you can drop into whenever you like. A typical game runs for around three hours.",
  },
];

const HouseRules = () => (
  <div data-testid="house-rules-page">
    <section className="lamp-glow mx-auto max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pt-44">
      <Eyebrow>The House Rules</Eyebrow>
      <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
        <MaskedLine delay={0.1}>A relaxed room still needs</MaskedLine>
        <MaskedLine delay={0.25} className="italic text-brass">
          clear rules.
        </MaskedLine>
      </h1>
      <FadeUp delay={0.4}>
        <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
          Four non-negotiables. They keep the room easy, safe and worth coming back to.
        </p>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-8">
      <FadeUp>
        <div className="relative overflow-hidden rounded-2xl border border-brass/15">
          <img
            src="/images/house-rules.png"
            alt="A phone placed face-down in a felt tray beside a brass key in warm lamplight"
            loading="lazy"
            className="aspect-[16/9] w-full object-cover sm:aspect-[21/8]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-transparent to-transparent" />
          <p className="absolute bottom-6 left-6 font-serif text-xl italic text-ivory sm:bottom-8 sm:left-10 sm:text-2xl">
            What happens in the Lounge stays in the Lounge.
          </p>
        </div>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-4xl px-5 pb-20 sm:px-8 sm:pb-28">
      <Accordion type="single" collapsible className="space-y-4">
        {RULES.map((rule, i) => (
          <FadeUp key={rule.title} delay={i * 0.05}>
            <AccordionItem
              value={`rule-${i}`}
              data-testid={`rule-accordion-${i}`}
              className="overflow-hidden rounded-2xl border border-brass/15 bg-charcoal/50 px-6 transition-colors hover:border-brass/35 sm:px-8"
            >
              <AccordionTrigger
                data-testid={`rule-trigger-${i}`}
                className="py-6 text-left hover:no-underline [&>svg]:text-brass"
              >
                <span className="flex items-center gap-4">
                  <span aria-hidden="true" className="text-xl text-brass">
                    {rule.suit}
                  </span>
                  <span className="font-serif text-xl text-ivory sm:text-2xl">{rule.title}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-7 pl-11 pr-2 text-base leading-relaxed text-ivory/70 sm:pl-12">
                {rule.body}
              </AccordionContent>
            </AccordionItem>
          </FadeUp>
        ))}
      </Accordion>

      <FadeUp delay={0.1}>
        <div className="mt-12 rounded-2xl border border-brass/15 bg-charcoal/40 p-8">
          <p className="text-sm leading-relaxed text-ivory/65">
            Something on your mind — a question, a concern, anything that didn't sit right at a
            gathering? We handle every message discreetly.
          </p>
          <Link
            to="/contact"
            data-testid="rules-contact-link"
            className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brass transition-colors hover:text-ivory"
          >
            Contact us discreetly
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </FadeUp>

      <FadeUp delay={0.15}>
        <Link
          to="/first-game"
          data-testid="rules-next-first-game-link"
          className="group mt-10 inline-flex items-center gap-3 rounded-xl border border-brass/30 px-7 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-brass transition-all duration-200 hover:border-brass hover:bg-brass/10"
        >
          Questions before your first game?
          <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </section>
  </div>
);

export default HouseRules;
